import * as CryptoJS from "crypto-js";

async function generateConsistentKey() {
  const uniqueIdentifier = navigator.userAgent; // or any other unique string for the device

  const encoder = new TextEncoder();
  const data = encoder.encode(uniqueIdentifier);

  // Hash the identifier using SHA-256
  const hashBuffer = await window.crypto.subtle.digest("SHA-256", data);

  // Convert the hash to a usable key format
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");

  const key = CryptoJS.enc.Hex.parse(hashHex);
  // Create a CryptoJS key from the hash
  const hexString = key?.words
    ?.map((num) => {
      // Convert to hex and pad with zeros if necessary, handling negative values as well
      return (num >>> 0).toString(16).padStart(8, "0");
    })
    .join("");

  localStorage.setItem("hexString", hexString);
  return hexString;
}

const unique_encryption_value = import.meta.env.VITE_SECRET_ENCRYPTION_KEY;
const ENCRYPTION_KEY = CryptoJS.enc.Utf8.parse(
  unique_encryption_value as unknown as string,
); // Must be 32 characters long
const IV_LENGTH = 16; // For AES, this is always 16

const encrypt = (plainText: string): string | null => {
  try {
    const iv = CryptoJS.lib.WordArray.random(IV_LENGTH);
    const encrypted = CryptoJS.AES.encrypt(plainText, ENCRYPTION_KEY, {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });

    // Combine IV and encrypted text for storage/transmission
    return iv.toString(CryptoJS.enc.Hex) + ":" + encrypted.toString();
  } catch (err) {
    console.error("Encryption error:", err);
    return null;
  }
};

const decrypt = (encrypted: string): string | null => {
  try {
    if (!encrypted) {
      throw new Error("Invalid encrypted text");
    }

    const textParts = encrypted.split(":");
    if (textParts.length !== 2) {
      throw new Error("Invalid encrypted text format");
    }

    const iv = CryptoJS.enc.Hex.parse(textParts[0]);
    const encryptedText = textParts[1];

    const decrypted = CryptoJS.AES.decrypt(encryptedText, ENCRYPTION_KEY, {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });

    return decrypted.toString(CryptoJS.enc.Utf8);
  } catch (err) {
    return null;
  }
};

const encryptBase64 = (raw: string) => {
  const secretKey = import.meta.env.VITE_SECRET_ENCRYPTION_KEY;
  const encrypted = CryptoJS.AES.encrypt(raw, secretKey).toString();
  return encrypted;
};

const decryptBase64 = (encrypted: string) => {
  const secretKey = import.meta.env.VITE_SECRET_ENCRYPTION_KEY;
  const bytes = CryptoJS.AES.decrypt(encrypted, secretKey);
  const decrypted = bytes.toString(CryptoJS.enc.Utf8);
  return decrypted;
};

//  NOTE: Deterministic IV is intentional here.
//  These IDs are MongoDB ObjectIds , already public/non-sensitive.
//  The goal is URL obfuscation, not cryptographic security.
//  Deterministic IV is required so the same id always produces
//  the same URL segment (needed for routing + reload).
export const encryptClientId = (id: string): string => {
  const iv = CryptoJS.MD5(id); // deterministic IV from id
  const key = CryptoJS.enc.Utf8.parse(unique_encryption_value.padEnd(32).slice(0, 32));

  const encrypted = CryptoJS.AES.encrypt(id, key, {
    iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  });

  // Use base64 for IV (shorter than hex)
  const ivB64 = iv.toString(CryptoJS.enc.Base64);
  const combined = ivB64 + ':' + encrypted.toString();

  //we need to replace some of these chars to not break the url structure
  //Example: / is path separator , + is space in url encoding,  = is key-value
  return combined
    .replace(/\//g, '_')
    .replace(/\+/g, '-')
    .replace(/=/g, '')
    .replace(/:/g, '.');
};

export const decryptClientId = (encrypted: string): string | null => {
  try {
    const restored = encrypted
      .replace(/\./g, ':')
      .replace(/_/g, '/')
      .replace(/-/g, '+');

    const parts = restored.split(':');
    if (parts.length !== 2) return null;

    const iv = CryptoJS.enc.Base64.parse(parts[0]);
    const key = CryptoJS.enc.Utf8.parse(unique_encryption_value.padEnd(32).slice(0, 32));

    const decrypted = CryptoJS.AES.decrypt(parts[1], key, {
      iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });

    const result = decrypted.toString(CryptoJS.enc.Utf8);

    if (!result || !/^[a-f0-9]{24}$/i.test(result)) return null;

    return result;
  } catch {
    return null;
  }
};

export default { encrypt, decrypt, encryptBase64, decryptBase64, encryptClientId, decryptClientId };
